import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DELETE_MESSAGE =
  "Are you sure you want to delete this item? This action cannot be undone.";

export type ConfirmDeleteOptions = {
  /** Shown in the dialog title, e.g. employee or vendor name */
  itemLabel?: string;
  /** Extra context below the standard message */
  detail?: string;
  onConfirm: () => void;
};

type ConfirmDeleteContextValue = {
  requestDelete: (options: ConfirmDeleteOptions) => void;
};

const ConfirmDeleteContext = createContext<ConfirmDeleteContextValue | null>(
  null,
);

export function ConfirmDeleteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<ConfirmDeleteOptions | null>(null);

  const requestDelete = useCallback((options: ConfirmDeleteOptions) => {
    setPending(options);
    setOpen(true);
  }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setPending(null);
  };

  const handleConfirm = () => {
    pending?.onConfirm();
    setOpen(false);
    setPending(null);
  };

  return (
    <ConfirmDeleteContext.Provider value={{ requestDelete }}>
      {children}
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.itemLabel
                ? `Delete ${pending.itemLabel}?`
                : "Confirm delete"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>{DELETE_MESSAGE}</p>
                {pending?.detail ? (
                  <p className="text-foreground/80">{pending.detail}</p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmDeleteContext.Provider>
  );
}

export function useConfirmDelete() {
  const ctx = useContext(ConfirmDeleteContext);
  if (!ctx) {
    throw new Error("useConfirmDelete must be used within ConfirmDeleteProvider");
  }
  return ctx;
}
